import { Link } from 'wouter';
import { useSession } from '@/auth/SessionContext';
import { useServiceStages } from '@/api/useServiceStages';
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageHeader,
  StageBadge,
} from '@/components/AppShell';
import { getStageLabelText, STAGE_TONE } from '@/utils/stageLabels';
import { maskPhone } from '@/utils/mask';
import { formatDateTime } from '@/utils/formatDate';

/**
 * 客户首页（Issue #198）：
 *   - 顶部欢迎卡片显示脱敏手机号 + 当前阶段。
 *   - 快捷入口跳转到进度 / 资料 / 报告 / 账单。
 *   - 不展示任何明文 PII；不在客户端推断"高风险"或"流失"等内部状态。
 */
export function HomePage() {
  const { account, signOut } = useSession();
  const { data, loading, notFound, error, refetch } = useServiceStages();

  return (
    <div>
      <PageHeader
        title="您好，欢迎使用金专客户中心"
        description="查看服务进度、上传资料、查看已审核报告。"
        trailing={
          <button
            type="button"
            onClick={() => void signOut()}
            className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
          >
            退出
          </button>
        }
      />
      <Card className="mb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-muted-foreground">登录账号</p>
            <p className="mt-1 text-sm font-medium">{maskPhone(account?.phone ?? '')}</p>
          </div>
          {data ? (
            <StageBadge
              label={getStageLabelText(data.currentStage)}
              tone={STAGE_TONE[data.currentStage]}
            />
          ) : null}
        </div>
        {data?.recentProgressSummary ? (
          <p className="mt-3 rounded-md bg-muted/60 p-3 text-sm">
            {data.recentProgressSummary}
          </p>
        ) : null}
        {data ? (
          <p className="mt-3 text-xs text-muted-foreground">
            更新于 {formatDateTime(data.updatedAt)}
          </p>
        ) : null}
      </Card>

      {error ? <ErrorBanner message={error.message} onRetry={refetch} /> : null}

      {loading ? (
        <Card>
          <p className="text-sm text-muted-foreground">服务进度加载中…</p>
        </Card>
      ) : notFound ? (
        <Card>
          <EmptyState
            title="客户档案尚未建立"
            description="请联系您的专属顾问开通客户档案后再回到本页面。"
          />
        </Card>
      ) : null}

      <div className="mt-4 grid grid-cols-2 gap-3">
        <Shortcut to="/progress" label="服务进度" />
        <Shortcut to="/documents" label="资料上传" />
        <Shortcut to="/reports" label="已审核报告" />
        <Shortcut to="/finance" label="账单状态" />
      </div>
    </div>
  );
}

function Shortcut({ to, label }: { to: string; label: string }) {
  return (
    <Link
      href={to}
      className="flex items-center justify-between rounded-lg border border-border bg-card p-3 text-sm font-medium shadow-sm hover:border-primary"
    >
      <span>{label}</span>
      <span aria-hidden className="text-muted-foreground">›</span>
    </Link>
  );
}
